import React, { useEffect, useState } from 'react';
import { authAPI } from '../services/api';
import { useHybridAuth } from '../contexts/HybridAuthContext';
import { getApiErrorMessage } from '../utils/authValidation';

interface Country {
  code: string;
  name: string;
  default_currency_code: string;
  default_date_format: string;
  default_number_format: string;
  default_time_format: string;
  default_first_day_of_week: number;
}

interface Currency {
  code: string;
  name: string;
  symbol: string;
}

const CompleteGoogleSignup: React.FC = () => {
  const { user, createBusiness, loading } = useHybridAuth();
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [businessData, setBusinessData] = useState({
    name: '',
    operational_name: '',
    email: user?.email || '',
    phone: '',
    address: '',
    tax_identification_number: '',
    country_code: '',
    base_currency_code: '',
    date_format: '',
    number_format: '',
    time_format: '',
    first_day_of_week: 1,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [countriesRes, currenciesRes] = await Promise.all([
          authAPI.getCountries(),
          authAPI.getCurrencies(),
        ]);
        if (countriesRes.data.success) {
          setCountries(countriesRes.data.data.countries);
        }
        if (currenciesRes.data.success) {
          setCurrencies(currenciesRes.data.data.currencies);
        }
      } catch (err) {
        console.error('Error fetching countries/currencies:', err);
      }
    };
    void load();
  }, []);

  useEffect(() => {
    if (user?.email) {
      setBusinessData((prev) => ({ ...prev, email: prev.email || user.email }));
    }
  }, [user?.email]);

  const handleCountryChange = (countryCode: string) => {
    const country = countries.find((c) => c.code === countryCode);
    if (!country) return;
    setBusinessData((prev) => ({
      ...prev,
      country_code: countryCode,
      base_currency_code: country.default_currency_code,
      date_format: country.default_date_format,
      number_format: country.default_number_format,
      time_format: country.default_time_format,
      first_day_of_week: country.default_first_day_of_week,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!businessData.name || !businessData.country_code) {
      setError('Business name and country are required');
      return;
    }

    try {
      await createBusiness(businessData);
      window.location.assign('/dashboard');
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to create business'));
    }
  };

  return (
    <div className="login-page">
      <div className="login-container" style={{ maxWidth: 480 }}>
        <div className="login-header">
          <h1>Set up your business</h1>
          <p>
            Welcome{user?.first_name ? `, ${user.first_name}` : ''}. Create your business to finish
            Google signup.
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="google_business_name">Business Name *</label>
            <input
              type="text"
              id="google_business_name"
              value={businessData.name}
              onChange={(e) => setBusinessData((prev) => ({ ...prev, name: e.target.value }))}
              required
              placeholder="Enter your business name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="google_business_email">Business Email</label>
            <input
              type="email"
              id="google_business_email"
              value={businessData.email}
              onChange={(e) => setBusinessData((prev) => ({ ...prev, email: e.target.value }))}
              placeholder="business@example.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="google_country">Country *</label>
            <select
              id="google_country"
              value={businessData.country_code}
              onChange={(e) => handleCountryChange(e.target.value)}
              required
            >
              <option value="">Select country</option>
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="google_currency">Currency</label>
            <select
              id="google_currency"
              value={businessData.base_currency_code}
              onChange={(e) =>
                setBusinessData((prev) => ({ ...prev, base_currency_code: e.target.value }))
              }
            >
              <option value="">Select currency</option>
              {currencies.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.code} — {currency.name}
                </option>
              ))}
            </select>
          </div>

          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? 'Creating…' : 'Create business & continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CompleteGoogleSignup;
